// src/components/admin/InvoiceEditModal.js
// Lets an admin/super_admin correct a booking's quantities after the fact, in the same
// description/qty/unit-price/amount layout as the actual invoice PDF. Every quantity change
// recalculates live via the shared pricing engine (calculateQuote) -- the same function the
// invoice itself is built from -- so what's shown here always matches what Save will produce.
import React, { useState } from "react";
import { CATEGORIES, MINIMUM_BOOKING, calculateQuote } from "../../utils/calculateTotal.js";
import apiClient from "../../utils/apiClient.js";
import Spinner from "../Spinner.js";
import "./InvoiceEditModal.css";

const cloneDetails = (details) => JSON.parse(JSON.stringify(details || {}));

const bookingCategories = (serviceType) => (Array.isArray(serviceType) ? serviceType : [serviceType]);

const InvoiceEditModal = ({ booking, onClose, onSaved }) => {
  const [details, setDetails] = useState(() => cloneDetails(booking.details));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const activeKeys = bookingCategories(booking.service_type);
  const activeCategories = CATEGORIES.filter((c) => activeKeys.includes(c.key));

  const activeDetails = {};
  activeCategories.forEach((c) => {
    activeDetails[c.key] = details[c.key] || {};
  });
  const quoteResult = calculateQuote(activeDetails);

  // Shows qty x unit price with no discount applied -- matches the invoice PDF, where the
  // discount is a separate, explicit "Special Discount" line rather than baked into each row.
  const amountFor = (categoryKey, fieldKey) =>
    quoteResult.lineItems.find((li) => li.category === categoryKey && li.field === fieldKey)?.standardTotal ?? 0;

  const handleQtyChange = (categoryKey, fieldKey, value) => {
    const qty = Math.max(0, Number(value) || 0);
    setDetails((prev) => ({
      ...prev,
      [categoryKey]: { ...prev[categoryKey], [fieldKey]: qty },
    }));
  };

  const handleSave = async () => {
    setError("");
    if (!quoteResult.meetsMinimum) return;

    setSaving(true);
    try {
      const { data } = await apiClient.put(`/bookings/${booking.id}/details`, { details: activeDetails });
      onSaved(data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="notify-modal-backdrop" onClick={saving ? undefined : onClose}>
      <div
        className="notify-modal invoice-edit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-edit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="invoice-edit-title">Edit Invoice — {booking.full_name}</h3>
        <p className="notify-modal-message invoice-edit-subtitle">
          Adjust quantities below — the total recalculates automatically using the same pricing
          rules as the invoice itself.
        </p>

        <div className="invoice-edit-table-wrap">
          <table className="invoice-edit-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {activeCategories.map((category) => (
                <React.Fragment key={category.key}>
                  {category.fields.map((field) => (
                    <tr key={`${category.key}-${field.key}`}>
                      <td>{field.label}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          disabled={saving}
                          value={details[category.key]?.[field.key] ?? 0}
                          onChange={(e) => handleQtyChange(category.key, field.key, e.target.value)}
                        />
                      </td>
                      <td>${field.unitPrice.toFixed(2)}</td>
                      <td>${amountFor(category.key, field.key).toFixed(2)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="invoice-edit-totals">
          <div className="invoice-edit-totals-row">
            <span>Service subtotal</span>
            <span>${quoteResult.serviceSubtotal.toFixed(2)}</span>
          </div>
          {quoteResult.quantityDiscounts > 0 && (
            <div className="invoice-edit-totals-row">
              <span>Special Discount</span>
              <span>-${quoteResult.quantityDiscounts.toFixed(2)}</span>
            </div>
          )}
          {quoteResult.orderDiscount > 0 && (
            <div className="invoice-edit-totals-row">
              <span>Order discount ({(quoteResult.orderDiscountRate * 100).toFixed(1)}%)</span>
              <span>-${quoteResult.orderDiscount.toFixed(2)}</span>
            </div>
          )}
          <div className="invoice-edit-totals-row invoice-edit-grand-total">
            <span>Grand Total</span>
            <span>${quoteResult.total.toFixed(2)}</span>
          </div>
        </div>

        {!quoteResult.meetsMinimum && (
          <p className="admin-error">Total must be at least ${MINIMUM_BOOKING.toFixed(2)} to save.</p>
        )}
        {error && <p className="admin-error">{error}</p>}

        <div className="invoice-edit-actions">
          <button type="button" className="admin-login-back-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving || !quoteResult.meetsMinimum}>
            {saving ? (
              <>
                <Spinner size="sm" inline /> Saving…
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceEditModal;
